import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export function ListingImageGenerator() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");

  const generateUniqueImages = async () => {
    setIsGenerating(true);
    setProgress(0);
    setStatus("Starting AI image generation...");

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error("You must be logged in");
        return;
      }

      setStatus("Generating unique images for all listings...");
      setProgress(10);

      const { data, error } = await supabase.functions.invoke('generate-unique-listing-images', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        }
      });

      if (error) throw error;

      setProgress(100);
      setStatus(`✓ Complete! Updated ${data.updated} listings`);
      
      toast.success(`Successfully generated ${data.updated} unique images!`, {
        description: data.failed > 0 ? `${data.failed} listings failed to update` : undefined
      });

      if (data.errors?.length > 0) {
        console.error("Failed listings:", data.errors);
      }

    } catch (error) {
      console.error("Error generating images:", error);
      toast.error("Failed to generate images", {
        description: error instanceof Error ? error.message : "Unknown error occurred"
      });
      setStatus("Error occurred");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          Generate Unique Listing Images
        </CardTitle>
        <CardDescription>
          Use AI to generate unique, professional images for all listings that currently use duplicate test images. 
          This will replace all /images/test-listings/* with custom AI-generated images.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isGenerating && (
          <div className="space-y-2">
            <Progress value={progress} />
            <p className="text-sm text-muted-foreground">{status}</p>
          </div>
        )}
        
        <Button 
          onClick={generateUniqueImages}
          disabled={isGenerating}
          size="lg"
          className="w-full"
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating Images...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              Generate Unique Images for All Listings
            </>
          )}
        </Button>

        <div className="text-xs text-muted-foreground space-y-1">
          <p>⚠️ This process may take several minutes depending on the number of listings.</p>
          <p>✓ Each listing will get a custom AI-generated image based on its title and sector.</p>
          <p>✓ Images will be stored in Supabase Storage and automatically applied.</p>
        </div>
      </CardContent>
    </Card>
  );
}
